import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Bell, 
  Check,
  CheckCheck,
  Trash2,
  Filter,
  Search,
  Flag,
  AlertTriangle,
  FileText,
  Calendar,
  MessageSquare,
  CheckCircle,
  Clock,
  ChevronLeft,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { apiRequest } from '../lib/utils';
import SwipeableNotificationCard from '../components/notifications/SwipeableNotificationCard';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const NOTIFICATION_ICONS = {
  md_attention: Flag,
  daily_summary: FileText,
  weekly_report: Calendar,
  task: CheckCircle,
  issue: AlertTriangle,
  message: MessageSquare,
  compliance_due: AlertTriangle,
  roster_submitted: Calendar,
  default: Bell
};

const NOTIFICATION_COLORS = {
  md_attention: 'bg-red-100 text-red-600 border-red-200',
  daily_summary: 'bg-blue-100 text-blue-600 border-blue-200',
  weekly_report: 'bg-purple-100 text-purple-600 border-purple-200',
  task: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  issue: 'bg-amber-100 text-amber-600 border-amber-200',
  message: 'bg-indigo-100 text-indigo-600 border-indigo-200',
  compliance_due: 'bg-orange-100 text-orange-600 border-orange-200',
  roster_submitted: 'bg-teal-100 text-teal-600 border-teal-200',
  default: 'bg-slate-100 text-slate-600 border-slate-200'
};

const NOTIFICATION_TYPE_LABELS = {
  md_attention: 'MD Attention',
  daily_summary: 'Daily Summary',
  weekly_report: 'Weekly Report',
  task: 'Task',
  issue: 'Issue',
  message: 'Message',
  compliance_due: 'Compliance',
  roster_submitted: 'Roster',
  default: 'General'
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [groupByConversation, setGroupByConversation] = useState(true);

  const isMessageType = (t) => ['new_message', 'group_message', 'group_mention', 'new_chat', 'chat_message', 'mention'].includes(t);

  const AVATAR_COLORS = [
    'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500',
    'bg-teal-500', 'bg-sky-500', 'bg-indigo-500', 'bg-purple-500',
    'bg-pink-500', 'bg-rose-500'
  ];
  const getAvatarBg = (name) => {
    if (!name) return 'bg-slate-500';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };

  // Group notifications by conversation when toggle is on
  const displayedNotifications = useMemo(() => {
    if (!groupByConversation) {
      return notifications.map(n => ({ ...n, _groupTotal: 1, _groupUnread: n.is_read ? 0 : 1, _groupIds: [n.id], _groupUnreadIds: n.is_read ? [] : [n.id] }));
    }
    const keyOf = (n) => {
      const m = n.metadata || {};
      return m.conversation_id || m.group_id || null;
    };
    const result = [];
    const seen = new Set();
    for (const n of notifications) {
      const key = keyOf(n);
      const isMsg = isMessageType(n.type);
      if (isMsg && key) {
        if (seen.has(key)) continue;
        seen.add(key);
        const siblings = notifications.filter(x => isMessageType(x.type) && keyOf(x) === key);
        const unread = siblings.filter(x => !x.is_read);
        result.push({
          ...n,
          _groupTotal: siblings.length,
          _groupUnread: unread.length,
          _groupIds: siblings.map(x => x.id),
          _groupUnreadIds: unread.map(x => x.id),
        });
      } else {
        result.push({ ...n, _groupTotal: 1, _groupUnread: n.is_read ? 0 : 1, _groupIds: [n.id], _groupUnreadIds: n.is_read ? [] : [n.id] });
      }
    }
    return result;
  }, [notifications, groupByConversation]);

  const fetchNotifications = useCallback(async () => {
    try {
      const unreadOnly = filter === 'unread';
      const data = await apiRequest(`/notifications?limit=100&unread_only=${unreadOnly}`);
      
      let notifList = data.notifications || [];
      
      // Apply read filter
      if (filter === 'read') {
        notifList = notifList.filter(n => n.is_read);
      }
      
      // Apply type filter
      if (typeFilter !== 'all') {
        notifList = notifList.filter(n => n.type === typeFilter);
      }
      
      // Apply search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        notifList = notifList.filter(n => 
          n.title?.toLowerCase().includes(query) || 
          n.message?.toLowerCase().includes(query)
        );
      }
      
      setNotifications(notifList);
      setStats({
        total: data.total || notifList.length,
        unread: data.unread_count || 0
      });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter, typeFilter, searchQuery]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchNotifications();
  };

  const markAsRead = async (notificationId, extraIds = []) => {
    try {
      const allIds = [notificationId, ...extraIds.filter(id => id !== notificationId)];
      await Promise.all(allIds.map(id =>
        apiRequest(`/notifications/${id}/read`, { method: 'POST' })
      ));
      setNotifications(prev => 
        prev.map(n => allIds.includes(n.id) ? { ...n, is_read: true } : n)
      );
      // Count how many were actually unread
      const wereUnread = notifications.filter(n => allIds.includes(n.id) && !n.is_read).length;
      setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - wereUnread) }));
      window.dispatchEvent(new Event('notifications-updated'));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      // Immediately tell bell to zero out
      window.dispatchEvent(new Event('notifications-cleared'));
      await apiRequest('/notifications/read-all', { method: 'PUT' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setStats(prev => ({ ...prev, unread: 0 }));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
      // Refetch on error to get correct count
      window.dispatchEvent(new Event('notifications-updated'));
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await apiRequest(`/notifications/${notificationId}`, { method: 'DELETE' });
      const deleted = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deleted && !deleted.is_read) {
        setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
      }
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const clearAllNotifications = async () => {
    if (!window.confirm('Are you sure you want to delete all notifications?')) return;
    
    try {
      await apiRequest('/notifications', { method: 'DELETE' });
      setNotifications([]);
      setStats({ total: 0, unread: 0 });
      toast.success('All notifications cleared');
      window.dispatchEvent(new Event('notifications-updated'));
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIcon = (type) => {
    return NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.default;
  };

  const getColorClass = (type) => {
    return NOTIFICATION_COLORS[type] || NOTIFICATION_COLORS.default;
  };

  const getTypeLabel = (type) => {
    return NOTIFICATION_TYPE_LABELS[type] || NOTIFICATION_TYPE_LABELS.default;
  };

  // Get unique notification types for filter
  const notificationTypes = [...new Set(notifications.map(n => n.type))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-3 sm:space-y-4 bg-slate-50 min-h-screen" data-testid="notifications-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Bell className="h-5 w-5" /> Notifications
            </h1>
            <p className="text-xs text-slate-600">
              {stats.unread > 0 ? `${stats.unread} unread` : 'All caught up!'} &middot; {stats.total} total
            </p>
          </div>
        </div>
        {/* Action buttons - always visible */}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          {stats.unread > 0 && (
            <Button size="sm" variant="outline" onClick={markAllAsRead} className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100" data-testid="mark-all-read-btn">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark All Read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button size="sm" variant="outline" onClick={clearAllNotifications} className="text-red-600 hover:text-red-700">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear All
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">Filters:</span>
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {notificationTypes.map(type => (
                  <SelectItem key={type} value={type}>{getTypeLabel(type)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex-1 max-w-xs">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none" data-testid="group-by-conversation-toggle">
              <input
                type="checkbox"
                checked={groupByConversation}
                onChange={(e) => setGroupByConversation(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer"
              />
              Group by conversation
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">
            {filter === 'unread' ? 'Unread Notifications' : 
             filter === 'read' ? 'Read Notifications' : 'All Notifications'}
            <Badge variant="outline" className="ml-2">{displayedNotifications.length}</Badge>
            {groupByConversation && displayedNotifications.length !== notifications.length && (
              <Badge variant="outline" className="ml-2 bg-slate-50 text-slate-500 text-xs">
                {notifications.length} total, grouped
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {displayedNotifications.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bell className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium mb-1">No notifications</h3>
              <p className="text-sm">
                {filter === 'unread' ? "You're all caught up!" : 
                 searchQuery ? 'No notifications match your search' : 
                 'No notifications yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {displayedNotifications.map((notification) => {
                const Icon = getIcon(notification.type);
                const colorClass = getColorClass(notification.type);
                const meta = notification.metadata || {};
                const isMsg = isMessageType(notification.type);
                const senderName = meta.sender_name || notification.title || '';
                const initial = (senderName.trim()[0] || '?').toUpperCase();
                const avatarBg = getAvatarBg(senderName);
                const groupUnread = notification._groupUnread || 0;
                const groupTotal = notification._groupTotal || 1;
                const hasMore = groupTotal > 1;
                
                return (
                  <SwipeableNotificationCard
                    key={notification.id}
                    canSwipeRight={groupUnread > 0}
                    onSwipeRight={() => {
                      const ids = notification._groupUnreadIds || [notification.id];
                      markAsRead(ids[0], ids.slice(1));
                    }}
                    onSwipeLeft={() => deleteNotification(notification.id)}
                  >
                  <div
                    className={`p-3 sm:p-4 rounded-lg border transition-all hover:shadow-md cursor-pointer ${
                      groupUnread > 0 ? 'bg-blue-50/50 border-blue-200' : 'bg-white border-slate-200'
                    }`}
                    onClick={() => {
                      if (notification._groupUnreadIds && notification._groupUnreadIds.length > 0) {
                        markAsRead(notification._groupUnreadIds[0], notification._groupUnreadIds.slice(1));
                      }
                      if (notification.link) navigate(notification.link);
                    }}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3 sm:gap-4">
                      {/* Avatar/Icon */}
                      {isMsg ? (
                        <div className="relative flex-shrink-0">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-semibold ${avatarBg}`}>
                            {initial}
                          </div>
                          {groupUnread > 1 && (
                            <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                              {groupUnread > 9 ? '9+' : groupUnread}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border-2 ${colorClass}`}>
                          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h4 className={`text-sm sm:text-base font-semibold break-words ${groupUnread > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                                {notification.title}
                              </h4>
                              {groupUnread > 0 && (
                                <span className="h-5 px-2 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center">
                                  {groupUnread > 1 ? `${groupUnread} new` : 'new'}
                                </span>
                              )}
                              <Badge variant="outline" className="text-[10px] sm:text-xs hidden sm:inline-flex">
                                {getTypeLabel(notification.type)}
                              </Badge>
                            </div>
                            <p className="text-sm sm:text-base text-slate-600 mb-1 break-words line-clamp-2 sm:line-clamp-none">{notification.message}</p>
                            {hasMore && (
                              <p className="text-xs text-slate-400 mb-2">
                                +{groupTotal - 1} earlier message{groupTotal - 1 > 1 ? 's' : ''} in this conversation
                              </p>
                            )}
                            <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTimeAgo(notification.created_at)}
                              </span>
                              <span className="text-slate-400 hidden sm:inline">•</span>
                              <span className="hidden sm:inline">{formatFullDate(notification.created_at)}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                            {groupUnread > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const ids = notification._groupUnreadIds || [notification.id];
                                  markAsRead(ids[0], ids.slice(1));
                                }}
                                className="text-blue-600 hover:text-blue-800 px-2 sm:px-3"
                                aria-label="Mark as read"
                                data-testid={`mark-read-${notification.id}`}
                              >
                                <Check className="h-4 w-4 sm:mr-1" />
                                <span className="hidden sm:inline">Mark Read</span>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                              className="text-slate-400 hover:text-red-600 px-2 sm:px-3"
                              aria-label="Delete notification"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </SwipeableNotificationCard>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
